import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from 'src/app/core/common-services/notification.service';
import { ChangeEmailDto, SendOTPMailRequest } from 'src/app/core/data-models/user.data.models';
import { UserDataService } from 'src/app/core/data-services/identity/user.data.service';
import { registrationEmailValidator } from 'src/app/shared/shared/registration-email-validator';
import { SharedModule } from '../../shared/shared.module';

@Component({
  selector: 'app-edit-email',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './edit-email.component.html',
  styleUrls: ['./edit-email.component.css']
})
export class EditEmailComponent implements OnInit {
  @Input() currentEmail: string = '';  
  @Input() phoneNumber: string = '';
  @Output() updated = new EventEmitter<string>(); 

  emailForm: FormGroup;
  submitted = false;
  step: 'form' | 'otp' = 'form';
  apiError: string = '';
  /** Address SendOTPMail actually used. Confirmed accounts receive the code on the current email. */
  otpDestinationEmail = '';  

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private userService: UserDataService,
    private notification: NotificationService,
  ) {
    this.emailForm = this.fb.group({
      currentEmail: ['', [Validators.required, Validators.email, this.emailsMatchValidator.bind(this)]],
      newEmail: ['', [Validators.required, registrationEmailValidator]],
    });
  }

  ngOnInit(): void {
    const userData = localStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      this.phoneNumber = user.phoneNumber || '';
    }
  }

  get f(): { [key: string]: AbstractControl } {
    return this.emailForm.controls;
  }

  onSubmit(): void {
    this.submitted = true;
    this.apiError = '';

    if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      return;
    }

    const request: SendOTPMailRequest = { newEmail: this.emailForm.value.newEmail };

    this.userService.SendOTPMail(request).subscribe({
      next: (res) => {
        const sentTo = (res?.resource || '').trim();
        this.otpDestinationEmail = sentTo || this.emailForm.value.newEmail;
        this.step = 'otp';
      },
      error: (err) => {
        this.apiError = err?.error?.message || 'Failed to send OTP';
      }
    });
  }

  emailsMatchValidator(control: AbstractControl) {
    const typedEmail = control.value;
    return typedEmail === this.currentEmail ? null : { emailMismatch: true };
  }

  handleOtpSuccess(otp: string) {
    this.submitted = true;
    this.apiError = '';

    if (this.emailForm.valid) {
      const request: ChangeEmailDto = {
        currentEmail: this.emailForm.value.currentEmail,
        newEmail: this.emailForm.value.newEmail,
        otp
      };

      this.userService.ChangeEmail(request).subscribe({
        next: () => {
          const newEmail = request.newEmail;
          this.patchStoredUser({ email: newEmail });
          this.notification.success('emailUpdateSuccess');
          this.updated.emit(newEmail);
          this.step = 'form';
          this.emailForm.reset();
        },
        error: (err) => {
          this.apiError = err?.error?.message || 'Failed to change email';
        }
      });
    }
  }

  handleOtpFailed(otp: string) {
    this.apiError = 'OTP verification failed';
  }

  private patchStoredUser(patch: { phoneNumber?: string; email?: string }): void {
    const raw = localStorage.getItem('currentUser');
    if (!raw) return;
    try {
      localStorage.setItem('currentUser', JSON.stringify({ ...JSON.parse(raw), ...patch }));
    } catch {
      // Leave the stored session unchanged if it is not valid JSON.
    }
  }
}
